<template>
  <base-radio
    v-bind="{ value, name, disabled }"
    v-model="modelValue"
    class="align-top i-flex"
    :class="[
      brandClasses.wrapper.shared,
      { 'cursor-pointer': !disabled },
    ]"
    data-test-id="vf-radio"
    @change="$emit('change', $event)"
  >
    <span
      class="shrink-0 rounded-full ring ring-inset duration peer-focus-visible:outline-auto"
      :class="[
        brandClasses.control.shared,
        {
          [controlClasses.default]: !checked && !disabled && !invalid,
          [controlClasses.selected]: checked && !disabled,
          [controlClasses.error]: invalid,
          [controlClasses.disabled]: !checked && disabled,
          [controlClasses.disabledSelected]: checked && disabled,
        },
        { 'sr-only': hideControl },
      ]"
      data-test-id="vf-radio-input"
    >
      <span
        class="block full rounded-full transition-opacity"
        :class="checked ? 'op-100' : 'op-0'"
        style="background-color: currentColor"
      />
    </span>
    <span
      v-if="$slots.default"
      class="w-full transition-colors"
      :class="[
        brandClasses.label.shared,
        disabled ? labelClasses.disabled : labelClasses.default,
      ]"
      data-test-id="vf-radio-label"
    >
      <!-- @slot Default slot mostly meant for label -->
      <slot />
    </span>
  </base-radio>
</template>

<script lang="ts" setup>
const { value, variant, hideControl = false } = defineProps<{
  /**
   * Equal to native 'value' attribute of input:radio
   */
  value: string
  /**
   * Equal to native 'name' attribute of input:radio
   */
  name?: string
  /**
   * Defines disabled state. Means radio button becomes inactive
   */
  disabled?: boolean
  /**
   * Defines invalid state
   */
  invalid?: boolean
  /**
   * Defines variant
   */
  variant?: 'inverse'
  /**
   * Makes the radio button control visible only for screen readers
   */
  hideControl?: boolean
}>()

defineEmits<{ change: [value: string] }>()

const modelValue = defineModel<string>()

const { brandClasses } = useAppConfig().components.vf.radio

const checked = computed(() => modelValue.value === value)

const controlClasses = computed(() => brandClasses.control.variant[variant || 'base'])
const labelClasses = computed(() => brandClasses.label.variant[variant || 'base'])
</script>
